import { isAxiosError, isCancel } from 'axios'
import { readonly, ref } from 'vue'

import { isDemoMode } from '@/lib/helpers'

const CHECK_CONTENT = 'metapic-connection-check'
const CHECK_TIMEOUT_MS = 3000
const CHECK_COOLDOWN_MS = 30_000
const suspected = ref(false)
let dismissed = false

export const adBlockerSuspected = readonly(suspected)

export const dismissAdBlockerWarning = (): void => {
  dismissed = true
  suspected.value = false
}

let pendingCheck: Promise<void> | undefined
let lastCheckAt: number | undefined

type CheckResult = 'available' | 'network-error' | 'inconclusive'

const checkResponseContent = async (response: Response): Promise<CheckResult> => {
  try {
    const content = await response.text()
    return content.trim() === CHECK_CONTENT ? 'available' : 'inconclusive'
  } catch {
    return 'inconclusive'
  }
}

const checkResource = async (url: string): Promise<CheckResult> => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    })

    if (!response.ok) {
      return 'inconclusive'
    }

    return await checkResponseContent(response)
  } catch (error) {
    const checkTimedOut = controller.signal.aborted
    if (checkTimedOut) {
      return 'inconclusive'
    }

    const isFetchNetworkError = error instanceof TypeError
    if (isFetchNetworkError) {
      return 'network-error'
    }

    return 'inconclusive'
  } finally {
    clearTimeout(timeout)
  }
}

const shouldCheck = (error: unknown): boolean =>
  !isDemoMode &&
  !dismissed &&
  !suspected.value &&
  navigator.onLine &&
  isAxiosError(error) &&
  !isCancel(error) &&
  !error.response &&
  error.code === 'ERR_NETWORK'

const detectPossibleBlocking = async (): Promise<boolean> => {
  const [control, probe] = await Promise.all([
    checkResource('/connection-check.txt'),
    checkResource('/ads/advertiser-check.txt?advertiser_id=1'),
  ])

  return navigator.onLine && control === 'available' && probe === 'network-error'
}

export const observeAdBlockerFailure = (error: unknown): Promise<void> => {
  if (!shouldCheck(error)) {
    return Promise.resolve()
  }

  if (pendingCheck !== undefined) {
    return pendingCheck
  }

  const isCheckCooldownActive =
    lastCheckAt !== undefined && Date.now() - lastCheckAt < CHECK_COOLDOWN_MS
  if (isCheckCooldownActive) {
    return Promise.resolve()
  }
  lastCheckAt = Date.now()

  pendingCheck = detectPossibleBlocking()
    .then((blockingDetected) => {
      if (blockingDetected && !dismissed) {
        suspected.value = true
      }
    })
    .finally(() => {
      pendingCheck = undefined
    })

  return pendingCheck
}
